import React from 'react';
import { Gift, Heart, Search, Star, TrendingUp } from 'lucide-react';

const HomePage = ({ setCurrentPage }) => {
  const featuredCategories = [
    { id: 'wedding', name: 'Bryllupsgaver', icon: '💍', description: 'Romantiske gaver for den store dagen' },
    { id: 'birthday', name: 'Bursdagsgaver', icon: '🎂', description: 'Personlige gaver for alle aldre' },
    { id: 'christmas', name: 'Julegaver', icon: '🎄', description: 'Koselige gaver til julebordet' },
    { id: 'electronics', name: 'Elektronikk', icon: '📱', description: 'Tech-gaver som imponerer' }
  ];

  const popularRecipients = [
    { id: 'for-him', name: 'Gaver til han', icon: '👨' },
    { id: 'for-her', name: 'Gaver til henne', icon: '👩' },
    { id: 'for-kids', name: 'Gaver til barn', icon: '👶' },
    { id: 'for-teachers', name: 'Gaver til lærere', icon: '👩‍🏫' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-purple-50 to-indigo-50 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-6xl animate-pulse">🎁</div>
          <div className="absolute top-20 right-20 text-4xl animate-bounce">💝</div>
          <div className="absolute bottom-20 left-20 text-5xl animate-pulse">🎉</div>
          <div className="absolute bottom-10 right-10 text-6xl animate-bounce">🎂</div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6 animate-bounce">🎁</div>
            <h1 className="text-6xl font-bold text-secondary-800 mb-6">
              Finn den <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-purple-600">perfekte</span> gaven
            </h1>
            <p className="text-xl text-secondary-600 mb-8 max-w-2xl mx-auto">
              La oss hjelpe deg å finne gaver som virkelig gleder. Personlige anbefalinger basert på 
              mottaker, anledning og ditt budsjett.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg"
              >
                <Gift className="w-6 h-6 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('category')}
                className="px-8 py-4 border-2 border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-semibold text-lg"
              >
                <Search className="w-6 h-6 inline mr-2" />
                Utforsk kategorier
              </button>
            </div>

            <div className="flex items-center justify-center space-x-8 text-secondary-600">
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-warning-500" />
                <span>Personlige forslag</span>
              </div>
              <div className="flex items-center space-x-2">
                <Heart className="w-5 h-5 text-error-500" />
                <span>Kurerte produkter</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-success-500" />
                <span>Oppdaterte trends</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Populære gavekategorier
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setCurrentPage(category.id)}
                  className="bg-white border border-secondary-200 rounded-lg p-6 hover:shadow-lg hover:border-primary-300 transition-all transform hover:scale-105 group"
                >
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                    {category.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-secondary-800 mb-2 group-hover:text-primary-600">
                    {category.name}
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    {category.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recipients Section */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Finn gaver etter mottaker
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {popularRecipients.map((recipient) => (
                <button
                  key={recipient.id}
                  onClick={() => setCurrentPage(recipient.id)}
                  className="bg-white border border-secondary-200 rounded-lg p-6 hover:shadow-md hover:border-purple-300 transition-all group text-center"
                >
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                    {recipient.icon}
                  </div>
                  <h3 className="font-semibold text-secondary-800 group-hover:text-purple-600">
                    {recipient.name}
                  </h3>
                </button>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <button
                onClick={() => setCurrentPage('recipient')}
                className="px-6 py-3 border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
              >
                Se alle mottakere
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Slik fungerer det
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">1️⃣</span>
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-2">Beskriv mottakeren</h3>
                <p className="text-secondary-600">
                  Fortell oss om personen du skal gi gave til - alder, interesser og relasjon.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">2️⃣</span>
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-2">Velg budsjett</h3>
                <p className="text-secondary-600">
                  Sett et budsjett som passer for deg, fra små oppmerksomheter til store gaver.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">3️⃣</span>
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-2">Få personlige forslag</h3>
                <p className="text-secondary-600">
                  Motta kurerte gaveforslag som passer perfekt til mottakeren og anledningen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte gaven?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              La oss hjelpe deg å glede noen du bryr deg om
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Start gavefinneren nå
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;